import React from "react";
import {observer} from "mobx-react-lite";
import {Box, ButtonBase, Stack, Typography} from "@mui/material";
import * as d3 from "d3";
import {useStores} from "~/stores";
import {TreeMap} from "~/components/visualization/tree-map/TreeMap";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {categoryPathPatch} from "~/stores/managers/spend-landscape/spendLandscapeFilters";
import {toTrimmedCategoryArray} from "~/services/classes/CategoryHelpers";
import {ChartCard} from "./ChartCard";
import {SPEND_RAMP} from "./chartHelpers";
import {categoryChildren, categoryDisplayLabel, categoryNode, categoryTotal} from "~/pages/spend-landscape/filters/categoryOptions";

/**
 * Treemap of the spend per category, with the scope and a legend of the categories at the current level.
 */
export const SpendByCategoryCard: React.FC = observer(() => {
    const {bagStore} = useStores();
    const manager = bagStore.advanced;
    const tree = manager.treeData;
    const path = toTrimmedCategoryArray(manager.singleSelectedCategory);
    const children = categoryChildren(tree, path)
        .map(c => ({label: c.label, total: categoryTotal(c)}))
        .sort((a, b) => b.total - a.total);
    // The root's own value is the sum of the L1s, so for "All" only its children count
    const node = categoryNode(tree, path);
    const scopeTotal = path.length === 0 ? children.reduce((sum, c) => sum + c.total, 0) : (node ? categoryTotal(node) : 0);
    const scopeLabel = path.length === 0 ? 'All' : path.map(categoryDisplayLabel).join(' › ');
    // Same scale as the treemap tiles, so a legend swatch matches its tile
    const extent = d3.extent(children, c => c.total) as [number, number] | [undefined, undefined];
    const swatch = d3.scaleLinear<string>().domain(extent[0] === undefined ? [0, 1] : extent as [number, number]).range(SPEND_RAMP);

    return <ChartCard
        title="Spend by category"
        actions={<Typography variant="body2" color="text.secondary" noWrap className="spend-landscape-scope">
            {scopeLabel} · <CurrencyComponent v={scopeTotal} nDigits={3}/>
        </Typography>}>
        <TreeMap
            data={tree}
            selectedCategory={manager.singleSelectedCategory ?? null}
            categoryFilterChanged={manager.onTreeMapCategoryFilterChanged}
            colorRange={SPEND_RAMP}
        />
        {children.length > 0 && <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={0.5}
                                       className="spend-landscape-legend">
            {children.map(c =>
                <ButtonBase
                    key={c.label}
                    className="spend-landscape-legend-item"
                    title={`Filter on ${categoryDisplayLabel(c.label)}`}
                    onClick={() => manager.filterStore.apply(categoryPathPatch([...path, c.label]))}>
                    <Box className="spend-landscape-legend-swatch" sx={{background: swatch(c.total)}}/>
                    <Typography variant="body2">{categoryDisplayLabel(c.label)}</Typography>
                    <Typography variant="body2" fontWeight={600} className="tabular">
                        <CurrencyComponent v={c.total} nDigits={3}/>
                    </Typography>
                </ButtonBase>
            )}
        </Stack>}
    </ChartCard>;
});
